<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  password: string
}>()

type Strength = 'weak' | 'fair' | 'strong'

const strength = computed<Strength>(() => {
  const p = props.password
  if (!p || p.length < 10) return 'weak'

  let types = 0
  if (/[a-z]/.test(p)) types++
  if (/[A-Z]/.test(p)) types++
  if (/[0-9]/.test(p)) types++
  if (/[^a-zA-Z0-9]/.test(p)) types++

  if (types >= 3) return 'strong'
  if (types >= 2) return 'fair'
  return 'weak'
})

const segments = computed(() => {
  const filled = strength.value === 'strong' ? 3 : strength.value === 'fair' ? 2 : 1
  return [1, 2, 3].map((i) => ({
    filled: i <= filled,
    color:
      strength.value === 'strong'
        ? 'bg-emerald-500'
        : strength.value === 'fair'
          ? 'bg-amber-500'
          : 'bg-red-500',
  }))
})

const label = computed(() => {
  if (strength.value === 'strong') return { text: 'Strong', class: 'text-emerald-400' }
  if (strength.value === 'fair') return { text: 'Fair', class: 'text-amber-400' }
  return { text: 'Weak', class: 'text-red-400' }
})
</script>

<template>
  <div v-if="password.length > 0" class="mt-1.5">
    <div class="flex gap-1">
      <div
        v-for="(seg, i) in segments"
        :key="i"
        class="h-1 flex-1 rounded-full transition-colors"
        :class="seg.filled ? seg.color : 'bg-slate-700'"
      />
    </div>
    <p class="mt-1 text-xs" :class="label.class">{{ label.text }}</p>
  </div>
</template>
